import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "toggle",        // the mobile hamburger button
    "icon",          // the 3-stripe icon wrapper
    "collapsible",   // the collapsible nav container
    "dropdownToggle",// each dropdown button
    "dropdownMenu"   // each dropdown menu
  ];

  connect() {
    this._setExpanded(this.toggleTarget, false);
    this._hide(this.collapsibleTarget);

    this.dropdownToggleTargets.forEach((btn) => this._setExpanded(btn, false));
    this.dropdownMenuTargets.forEach((menu) => this._hide(menu));
  }

  // === Mobile menu ===
  toggleMenu(event) {
    event.preventDefault();

    const isOpen = this.collapsibleTarget.classList.contains("show");
    if (isOpen) {
      this._hide(this.collapsibleTarget);
      this._setExpanded(this.toggleTarget, false);
      this._iconActive(false);
      this._closeAllDropdowns();
    } else {
      this._show(this.collapsibleTarget);
      this._setExpanded(this.toggleTarget, true);
      this._iconActive(true);
    }
  }

  // === Dropdowns ===
  toggleDropdown(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const menu = this._menuFor(button);
    if (!menu) return;

    const isOpen = menu.classList.contains("show");
    this._closeAllDropdowns({ except: menu });

    if (isOpen) {
      this._hide(menu);
      this._setExpanded(button, false);
    } else {
      this._show(menu);
      this._setExpanded(button, true);

      const firstLink = menu.querySelector("a,button,[tabindex]:not([tabindex='-1'])");
      if (firstLink) firstLink.focus();
    }
  }

  dropdownKeydown(event) {
    const key = event.key;
    if (key === " " || key === "Enter") {
      event.preventDefault();
      this.toggleDropdown(event);
    } else if (key === "Escape") {
      event.preventDefault();
      this._closeAllDropdowns();
      event.currentTarget.focus();
    }
  }

  // === Global handlers ===
  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this._closeAllDropdowns();
      if (this.collapsibleTarget.classList.contains("show")) {
        this._hide(this.collapsibleTarget);
        this._setExpanded(this.toggleTarget, false);
        this._iconActive(false);
      }
    }
  }

  handleGlobalKeydown(event) {
    if (event.key === "Escape") {
      this._closeAllDropdowns();
      if (this.collapsibleTarget.classList.contains("show")) {
        this._hide(this.collapsibleTarget);
        this._setExpanded(this.toggleTarget, false);
        this._iconActive(false);
        this.toggleTarget.focus();
      }
    }
  }

  // === Helpers ===
  _menuFor(button) {
    let sib = button.nextElementSibling;
    while (sib && !sib.classList.contains("dropdown-menu")) {
      sib = sib.nextElementSibling;
    }
    return sib;
  }

  _closeAllDropdowns({ except = null } = {}) {
    this.dropdownMenuTargets.forEach((menu) => {
      if (menu !== except) this._hide(menu);
    });
    this.dropdownToggleTargets.forEach((btn) => {
      if (!except || this._menuFor(btn) !== except) this._setExpanded(btn, false);
    });
  }

  _setExpanded(el, bool) {
    if (!el) return;
    el.setAttribute("aria-expanded", bool ? "true" : "false");
  }

  _show(el) {
    if (!el) return;
    el.classList.add("show");
    el.classList.remove("collapse");

    // add "open" with delay for transition effect
    setTimeout(() => {
      el.classList.add("open");
    }, 50);
  }

  _hide(el) {
    if (!el) return;
    el.classList.remove("show", "open");
    if (!el.classList.contains("collapse")) el.classList.add("collapse");
  }

  _iconActive(active) {
    if (!this.hasIconTarget) return;
    if (active) {
      setTimeout(() => this.iconTarget.classList.add("open"), 50);
    } else {
      this.iconTarget.classList.remove("open");
    }
  }
}
